import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, arrayOf, extend, bool
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductBadge, ProductSponsored,
  ProductImage, PodFooter, PodSection, ProductCompare, PodSpacer, ProductIdentifier
} from '@thd-olt-component-react/product-pod';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useAssistant } from '../../../AssistantProvider';

const AssistantProductPod = ({
  itemId,
  hoverDelay,
  onClick,
  disablePadding,
  index
}) => {
  const { store } = useContext(ExperienceContext);
  const { saveMessage } = useAssistant();
  const { ref, clickID } = useImpression({ data: {
    id: itemId,
    name: 'ProductPod',
    component: 'ProductPod',
    position: (index + 1) || 1,
    type: 'product'
  } });

  const handleRedirect = () => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-click', {
      displayPosition: `${index + 1}`,
    });
    onClick();
  };

  const handleAddToCart = (event) => {
    if (event?.output?.data?.addToCart) {
      window.LIFE_CYCLE_EVENT_BUS.off('add-to-cart.success', handleAddToCart);
      saveMessage({ type: 'add-to-cart', content: { itemId } });
    }
  };

  const atcOnClick = () => {
    const addedItem = {
      index,
      productID: itemId,
      displayPosition: `${index + 1}`,
    };

    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-atc-click', {
      items: [addedItem]
    });
    window.LIFE_CYCLE_EVENT_BUS.on('add-to-cart.success', handleAddToCart);
  };

  return (
    <ProductPod
      itemId={itemId}
      storeId={store.storeId}
      hoverDelay={hoverDelay}
      padding={disablePadding ? 'sui-p-0' : null}
      id="maa-recs-pod"
      ref={ref}
      clickid={clickID}
      render={(pod) => (
        <>
          <div className="sui-flex sui-justify-between sui-mb-2">
            <PodSection noPadding>
              <ProductBadge itemId={pod.itemId} storeId={store.storeId} />
              <ProductSponsored itemId={pod.itemId} />
            </PodSection>
            <PodSpacer padding={['TOP', 'RIGHT']}>
              <AddToList storeId={store.storeId} itemId={pod.itemId} showCount showIconButton />
            </PodSpacer>
          </div>
          <div className="sui-m-auto sui-flex sui-justify-center">
            <ProductImage
              itemId={pod.itemId}
              showSecondaryImage={pod.showSecondaryImage}
              onClick={handleRedirect}
            />
          </div>
          <PodSection columnAlign alignTop>
            <PodSpacer minHeight="60px" disable={pod.productSubType === 'HDQC'}>
              <Price
                itemId={pod.itemId}
                large={false}
                storeId={store.storeId}
                displayEachUom={false}
                onSavingsCenterToggle={pod.onSavingsCenterToggle}
                hideForProductTypes={['HDQC']}
              />
            </PodSpacer>
            <ProductRating itemId={pod.itemId} />
            <PodSpacer padding={['TOP', 'BOTTOM']}>
              <ProductHeader
                brand="above"
                itemId={pod.itemId}
                brandTitleMaxLine={4}
                onClick={handleRedirect}
                disableShopThisCollection
              />
            </PodSpacer>
          </PodSection>
          <PodFooter>
            <PodSpacer padding={['TOP']}>
              <ProductATC
                itemId={pod.itemId}
                storeId={store.storeId}
                checkGeneric
                doNotShowChooseYourOptions={false}
                onClick={atcOnClick}
                outline
              />
            </PodSpacer>
          </PodFooter>
        </>
      )}
    />
  );
};

AssistantProductPod.propTypes = {
  itemId: PropTypes.string.isRequired,
  hoverDelay: PropTypes.number,
  onClick: PropTypes.func,
  disablePadding: PropTypes.bool,
  index: PropTypes.number,
};

AssistantProductPod.defaultProps = {
  onClick: () => {},
  hoverDelay: 500,
  disablePadding: false,
  index: undefined
};

AssistantProductPod.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      details: shape({
        collection: shape({
          collectionId: string()
        }),
        highlights: arrayOf(string())
      }),
      info: shape({
        productDepartmentId: string(),
        paintBrand: string(),
        dotComColorEligible: bool(),
      }),
      pricing: params({ storeId: string() }).shape({
        promotion: shape({
          dates: shape({
            start: string(),
            end: string()
          })
        })
      }),
    })
  },
  ProductPod,
  ProductImage,
  AddToList,
  Price,
  ProductATC,
  ProductRating,
  ProductCompare,
  ProductHeader,
  ProductBadge,
  ProductIdentifier,
  ProductSponsored
);

export { AssistantProductPod };